import * as React from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { RichTextField } from "@/components/ui/rich-text-field"
import { useCmdEnterSubmit } from "@/lib/use-cmd-enter-submit"
import { useBlockAutoReload } from "@/lib/standalone-recovery"

// Comment composer pinned to the bottom of any activity feed. Pass the
// POST endpoint for the subject's events stream — pieces use
// `/pieces/:id/comments`.
export const ActivityComposer = React.forwardRef<HTMLDivElement, {
  eventsPath: string
  placeholder?: string
  disabled?: boolean
  disabledHint?: string
}>(function ActivityComposer(
  {
    eventsPath,
    placeholder = "Write a comment — markdown supported. ⌘↵ to post.",
    disabled = false,
    disabledHint,
  },
  ref,
) {
  const [draft, setDraft]       = React.useState("")
  const [resetKey, setResetKey] = React.useState(0)
  const [posting, setPosting]   = React.useState(false)

  // Block the standalone recovery reload while there's an unsent draft, so a
  // background suspend + resume can't discard a comment the user is typing.
  useBlockAutoReload(draft.trim().length > 0)

  // Refs so the keydown handler always reads the latest draft / posting
  // state without rebinding on every keystroke.
  const draftRef = React.useRef(draft)
  draftRef.current = draft
  const postingRef = React.useRef(posting)
  postingRef.current = posting

  const post = React.useCallback(() => {
    const body = draftRef.current.trim()
    if (body.length === 0 || postingRef.current || disabled) return
    setPosting(true)
    router.post(
      eventsPath,
      { body },
      {
        preserveScroll: false,
        onFinish: () => {
          setPosting(false)
          setDraft("")
          setResetKey((n) => n + 1)
        },
      },
    )
  }, [eventsPath, disabled])

  const onKeyDownCapture = useCmdEnterSubmit<HTMLDivElement>(post)

  if (disabled) {
    return (
      <div
        ref={ref}
        className="rounded-md border border-dashed border-hairline bg-surface p-4 text-center text-xs text-ink-muted"
      >
        {disabledHint ?? "Commenting isn't available here yet."}
      </div>
    )
  }

  return (
    <div ref={ref}>
      <div
        className="rounded-md border border-hairline bg-surface p-3"
        onKeyDownCapture={onKeyDownCapture}
      >
        <RichTextField
          key={resetKey}
          defaultValue=""
          onChange={setDraft}
          placeholder={placeholder}
        />
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            disabled={posting || draft.trim().length === 0}
            onClick={post}
          >
            {posting ? "Posting…" : "Post"}
          </Button>
        </div>
      </div>
    </div>
  )
})
